import { Box, IconButton, Typography } from "@mui/material"
import { EditOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useShallow } from "zustand/react/shallow"

import { PurchaseCostPackageLabel } from "../../types"

import {
  FREIGHT_TERMS_TO_LABEL,
  INCOTERMS_TO_LABEL,
  formatIncotermsLocationLabel,
  formatPurchaseCostDate,
  formatPurchaseCostDateShort,
  formatTierPrice,
  formatTierQty,
  formatUomLabel,
} from "./purchaseCostLabels"

import type { Incoterms, PurchasingCostListEntry } from "@/graphql/codegen/graphql"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"

interface PurchaseCostPackageCardProps {
  package: PurchasingCostListEntry
  label: PurchaseCostPackageLabel
  onEdit?: (entry: PurchasingCostListEntry) => void
  testIdPrefix?: string
}

const PILL_BASE_CLASSES =
  "flex items-center justify-center whitespace-nowrap rounded-[2px] px-2 py-1 text-[12px] leading-4"

export const PurchaseCostPackageCard: FC<PurchaseCostPackageCardProps> = ({
  package: pkg,
  label,
  onEdit,
  testIdPrefix = "product.purchase-costs.package-card",
}) => {
  const units = useUnitOfMeasurementsStore(useShallow((state) => state.units))
  const isFuture = label === PurchaseCostPackageLabel.Future
  const isHistoric = label === PurchaseCostPackageLabel.Historic
  const cardBgClass = isFuture ? "bg-gray-100" : "bg-white"
  const cardBorderClass = isHistoric ? "overflow-hidden rounded-md border border-gray-400" : ""
  const headerBgClass = isHistoric ? "bg-gray-300" : isFuture ? "bg-primary-200" : "bg-primary-100"
  const pillBgClass = isHistoric ? "bg-gray-400" : isFuture ? "bg-primary-300" : "bg-primary-200"
  const pillTextClass = isFuture ? "text-white" : "text-gray-700"
  const formatDate = isHistoric ? formatPurchaseCostDateShort : formatPurchaseCostDate
  const untilLabel = isHistoric ? "Until" : "Valid Until"
  const cardKey = pkg.id

  return (
    <Box
      className={`flex min-w-[500px] flex-1 shrink-0 flex-col ${cardBorderClass} ${cardBgClass}`}
      data-testid={`${testIdPrefix}-${cardKey}`}
    >
      <Box className={`flex items-center justify-between gap-x-3 p-4 ${headerBgClass}`}>
        <Box className='flex items-center gap-x-3 text-gray-700'>
          {!isHistoric && (
            <Typography variant='body2' className='font-medium'>
              {label}
            </Typography>
          )}
          <Box className={`${PILL_BASE_CLASSES} ${pillBgClass} ${pillTextClass} gap-x-2`}>
            <span className='font-bold tracking-[0.06px]'>Effective Date</span>
            <span className='font-normal tracking-[0.06px]'>{formatDate(pkg.effectiveDate)}</span>
          </Box>
          {pkg.expirationDate && (
            <Box className={`${PILL_BASE_CLASSES} ${pillBgClass} ${pillTextClass} gap-x-2`}>
              <span className='font-bold tracking-[0.06px]'>{untilLabel}</span>
              <span className='font-normal tracking-[0.06px]'>{formatDate(pkg.expirationDate)}</span>
            </Box>
          )}
        </Box>
        {onEdit && (
          <IconButton
            size='small'
            onClick={() => onEdit(pkg)}
            data-testid={`${testIdPrefix}-${cardKey}.edit-button`}
            aria-label='Edit package'
            className='!p-0 text-primary'
          >
            <EditOutlined fontSize='small' />
          </IconButton>
        )}
      </Box>
      <Box className='flex flex-col gap-y-1 p-4'>
        {pkg.tiers.map((tier, index) => (
          <Box key={`${cardKey}-tier-${index}`} className='flex items-center gap-x-48'>
            <Typography variant='body2' className='text-gray-700'>
              {formatTierQty(tier.qty)}
            </Typography>
            <Typography variant='body2' className='text-gray-700'>
              {formatTierPrice(tier.price, formatUomLabel(tier.uomId, units))}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box className='flex flex-col gap-y-1 border-t border-gray-400 p-4'>
        <Typography variant='body2' className='text-gray-700'>
          <span className='font-bold'>Freight: </span>
          {pkg.freightTerms ? FREIGHT_TERMS_TO_LABEL[pkg.freightTerms] : "—"}
        </Typography>
        <Typography variant='body2' className='text-gray-700'>
          <span className='font-bold'>Incoterms: </span>
          {pkg.incoterms ? (INCOTERMS_TO_LABEL[pkg.incoterms as Incoterms] ?? pkg.incoterms) : "—"}
        </Typography>
        <Typography variant='body2' className='text-gray-700'>
          <span className='font-bold'>Incoterms Location: </span>
          {pkg.incotermsLocation ? formatIncotermsLocationLabel(pkg.incotermsLocation) : "—"}
        </Typography>
      </Box>
    </Box>
  )
}
